import { useCallback, useEffect } from 'react'

import { Segment, User } from '../types'
import { getCaretPosition } from '../utils/domUtils'
import { normalizeSegments } from '../utils/segmentUtils'

import { UseEditorStateReturn } from './useEditorState'

export const useMentionLogic = (props: UseEditorStateReturn) => {
  const {
    segments,
    setSegments,
    activeIndex,
    setActiveIndex,
    nextCursorPos,
    elRefs,
    nextId,
    isProcessing,
    users,
    mentionQuery,
    setMentionQuery,
    setIsMentionPopupOpen,
    setPopupCoords,
    setFilteredUsers,
    shouldRefocusOnNavigate,
    setActiveSuggestionIndex,
  } = props

  const calculateAndSetPopupCoords = useCallback(
    (segmentId: string) => {
      const activeEl = elRefs.current.get(segmentId)
      if (!activeEl) return

      const rect = activeEl.getBoundingClientRect()
      setPopupCoords({
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
      })
    },
    [elRefs, setPopupCoords],
  )

  useEffect(() => {
    if (mentionQuery) {
      const filtered = users.filter(user =>
        user.name.toLowerCase().includes(mentionQuery.toLowerCase()),
      )
      setFilteredUsers(filtered)
      setActiveSuggestionIndex(0) // Always reset to the first suggestion
    } else {
      setFilteredUsers(users) // Show all users if query is empty
      setActiveSuggestionIndex(0) // Also reset here
    }
  }, [mentionQuery, users, setFilteredUsers, setActiveSuggestionIndex])

  const completeMention = useCallback(
    (user: User) => {
      const mentionSegmentInProgress = segments[activeIndex]
      const prevTextSegment = segments[activeIndex - 1]
      const mentionText = `@${user.name}`

      if (prevTextSegment && prevTextSegment.isModified) {
        prevTextSegment.isModified = false
      }

      const finalizedMentionSegment: Segment = {
        ...mentionSegmentInProgress,
        type: 'mention',
        content: mentionText,
        isModified: false,
      }

      const newSegments = [...segments]
      newSegments[activeIndex] = finalizedMentionSegment

      const spaceSegment: Segment = {
        id: `seg-${nextId.current++}`,
        type: 'text',
        content: ' ',
      }
      newSegments.splice(activeIndex + 1, 0, spaceSegment)

      const normalized = normalizeSegments(newSegments)
      const finalNewIndex = normalized.findIndex(s => s.id === spaceSegment.id)

      setSegments(normalized)

      setIsMentionPopupOpen(false)
      setMentionQuery('')
      nextCursorPos.current = 'end'
      setActiveIndex(
        finalNewIndex !== -1 ? finalNewIndex : normalized.length - 1,
      )

      shouldRefocusOnNavigate.current = true
      isProcessing.current = false // Unlock if no users found
    },
    [
      activeIndex,
      segments,
      setSegments,
      setActiveIndex,
      nextCursorPos,
      nextId,
      isProcessing,
      users,
      setMentionQuery,
      setIsMentionPopupOpen,
      calculateAndSetPopupCoords,
      shouldRefocusOnNavigate,
    ],
  )

  const cancelMention = useCallback(
    (addSpace = false) => {
      const mentionSegment = segments[activeIndex]
      if (!mentionSegment || mentionSegment.type !== 'mention_in_progress') {
        return
      }

      const newContent = mentionSegment.content + (addSpace ? ' ' : '')
      const newTextSegment: Segment = {
        ...mentionSegment,
        type: 'text',
        content: newContent,
      }

      const newSegments = [...segments]
      newSegments[activeIndex] = newTextSegment

      const normalized = normalizeSegments(newSegments)
      setSegments(normalized)

      const finalIndex = normalized.findIndex(s => s.id === newTextSegment.id)
      const finalSegment = normalized[finalIndex]

      if (finalSegment) {
        setActiveIndex(finalIndex)
        const cursorPosition = finalSegment.content.lastIndexOf(newContent) + newContent.length
        nextCursorPos.current = cursorPosition >= 0 ? cursorPosition : finalSegment.content.length
      } else {
        const prevIndex = Math.max(0, activeIndex - 1)
        setActiveIndex(prevIndex)
        nextCursorPos.current = normalized[prevIndex]?.content.length || 0
      }

      setIsMentionPopupOpen(false)
      setMentionQuery('')
      isProcessing.current = false
    },
    [
      activeIndex,
      segments,
      setSegments,
      setActiveIndex,
      nextCursorPos,
      setIsMentionPopupOpen,
      setMentionQuery,
      isProcessing,
    ],
  )
  
  const startMention = useCallback((
    index: number
  ): boolean => {
    isProcessing.current = true // Lock input processing

    const currentSegment = segments[index]
    const content = currentSegment.content
    const caretPos = getCaretPosition(elRefs.current.get(currentSegment.id)!)

    const beforeText = content.slice(0, caretPos)
    const afterText = content.slice(caretPos)

    const beforeSegment: Segment = { ...currentSegment, content: beforeText }
    const mentionSegment: Segment = {
      id: `seg-${nextId.current++}`,
      type: 'mention_in_progress',
      content: '@',
    }

    const newSegmentsWithMention = [...segments]

    if (afterText) {
      const afterSegment: Segment = { id: `seg-${nextId.current++}`, type: 'text', content: afterText }
      newSegmentsWithMention.splice(index, 1, beforeSegment, mentionSegment, afterSegment)
    } else {
      newSegmentsWithMention.splice(index, 1, beforeSegment, mentionSegment)
    }

    const normalized = normalizeSegments(newSegmentsWithMention)
    const finalNewIndex = normalized.findIndex(s => s.id === mentionSegment.id)

    setSegments(normalized)
    setActiveIndex(finalNewIndex !== -1 ? finalNewIndex : 0)
    nextCursorPos.current = 1

    // Now, open the suggestions popup
    const query = ''
    setMentionQuery(query)
    if (users.length > 0) {
      setIsMentionPopupOpen(true)
      setTimeout(() => calculateAndSetPopupCoords(mentionSegment.id), 0)
    } else {
      isProcessing.current = false // Unlock if no users found
    }

    setTimeout(() => {
      isProcessing.current = false
    }, 0)

    return true // Handled it!
  },
  [
    segments,
    setSegments,
    setActiveIndex,
    elRefs,
    nextId,
    nextCursorPos,
    isProcessing,
    users,
    setMentionQuery,
    setIsMentionPopupOpen,
    calculateAndSetPopupCoords,
  ])

  const updateMention = useCallback(
    (content: string) => {
      const query = content.substring(1)
      setMentionQuery(query)

      const currentSegment = segments[activeIndex]
      if (!currentSegment) return

      setIsMentionPopupOpen(true)
      setTimeout(() => calculateAndSetPopupCoords(currentSegment.id), 0)
    },
    [
      activeIndex,
      segments,
      setMentionQuery,
      setIsMentionPopupOpen,
      calculateAndSetPopupCoords,
    ],
  )

  const handleSelectUser = useCallback(
    (user: User) => {
      completeMention(user)
    },
    [completeMention],
  )

  return {
    cancelMention,
    startMention,
    updateMention,
    handleSelectUser,
  }
}

export type UseMentionLogicReturn = ReturnType<typeof useMentionLogic> 